import React from 'react';
import { Box, Chip, Paper, Skeleton, Typography, alpha, useTheme } from '@mui/material';

import { FiltroPorPeriodo } from '../FiltroPorPeriodo';
import type { FiltroPorPeriodoValue } from '../FiltroPorPeriodo';

interface DashboardChartCardShellProps {
  icon: React.ReactElement;
  title: string;
  subtitle: string;
  periodValue: FiltroPorPeriodoValue;
  onPeriodChange: (value: FiltroPorPeriodoValue) => void;
  totalSinceDate?: string | null;
  loading?: boolean;
  skeletonHeight?: number;
  children: React.ReactNode;
  /** Quando true, o gráfico segue o período global do dashboard. */
  useGlobalPeriod?: boolean;
  onUseGlobalPeriodChange?: (useGlobal: boolean) => void;
}

export function DashboardChartCardShell({
  icon,
  title,
  subtitle,
  periodValue,
  onPeriodChange,
  totalSinceDate,
  loading = false,
  skeletonHeight = 320,
  children,
  useGlobalPeriod,
  onUseGlobalPeriodChange,
}: DashboardChartCardShellProps) {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            display: 'grid',
            placeItems: 'center',
            flex: '0 0 auto',
          }}
        >
          {React.cloneElement(icon, {
            sx: { fontSize: 16, color: theme.palette.primary.main },
          })}
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#303030', textTransform: 'uppercase' }}>
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 1.5,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#808080' }}>{subtitle}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {onUseGlobalPeriodChange != null && (
            <Chip
              size="small"
              label={useGlobalPeriod ? 'Filtro global' : 'Período próprio'}
              color={useGlobalPeriod ? 'primary' : 'default'}
              variant={useGlobalPeriod ? 'filled' : 'outlined'}
              onClick={() => onUseGlobalPeriodChange(!useGlobalPeriod)}
              sx={{ height: 26, fontSize: 11, fontWeight: 700 }}
            />
          )}
          <FiltroPorPeriodo
            value={periodValue}
            onChange={onPeriodChange}
            label="FILTRAR POR:"
            compact
            totalSinceDate={totalSinceDate}
          />
        </Box>
      </Box>
      {loading ? (
        <Skeleton variant="rectangular" sx={{ flex: 1, minHeight: skeletonHeight }} />
      ) : (
        children
      )}
    </Paper>
  );
}
